import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Lock, Save, Award } from "lucide-react";

const NUM = [["months", "Must Achieve Within (months)"], ["nb_gfp", "NB GFP ($)"], ["pl_specialty", "PL & Specialty NB"], ["life_ip", "Life I&P"],
  ["bi_nb", "BI NB"], ["folio_bonus", "Folio Bonus ($)"], ["leads_bucks", "Leads Bucks ($)"], ["cumulative", "Cumulative Opportunity ($)"]];
const STATUS = { draft: "Draft", active: "Active", archived: "Archived" };

function CopyDialog({ src, onClose, onDone }) {
  const [year, setYear] = useState(String((src.year || new Date().getFullYear()) + 1));
  const [name, setName] = useState("");
  const go = async () => {
    try { const { data } = await api.post(`/${src.program}-bonus/versions`, { copy_from: src.id, year: Number(year), name: name || undefined }); toast.success("New schedule created as a draft"); onDone(data.id); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="bonus-copy-dialog">
        <DialogHeader><DialogTitle>Copy schedule to a new year</DialogTitle><DialogDescription>Creates a new draft version. The original and everyone assigned to it stay unchanged.</DialogDescription></DialogHeader>
        <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder="Year" data-testid="bonus-copy-year" />
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={`${year} ${src.title}`} data-testid="bonus-copy-name" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={go} className="bg-[#006090] hover:bg-[#004E75]" data-testid="bonus-copy-create">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Editor({ v, canManage, onSaved, onCopy }) {
  const [rows, setRows] = useState(v.rows);
  useEffect(() => { setRows(v.rows); }, [v]);
  const locked = !v.editable;
  const set = (i, k, val) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: val } : r)));
  const save = async (extra = {}) => {
    try {
      const body = locked ? extra : { rows: rows.map((r) => ({ ...r, ...Object.fromEntries(NUM.map(([k]) => [k, r[k] === "" || r[k] == null ? null : Number(r[k])])) })), ...extra };
      const { data } = await api.put(`/${v.program}-bonus/versions/${v.id}`, body); toast.success("Schedule saved"); onSaved(data.id);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="bonus-editor">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div>
          <p className="font-display text-lg font-bold text-slate-900">{v.name || `${v.year} ${v.title}`}</p>
          <p className="text-xs text-slate-500">{STATUS[v.status] || v.status} · {v.participants} participant(s) assigned{v.official ? " · Official document 326737 04/26" : ""}{v.copied_from ? " · copied from an earlier schedule" : ""}</p>
        </div>
        <div className="flex gap-2">
          {canManage && <Button variant="outline" onClick={onCopy} data-testid="bonus-copy-open"><Copy className="w-4 h-4 mr-1.5" />Copy to new year</Button>}
          {canManage && !v.official && v.status !== "active" && <Button variant="outline" onClick={() => save({ status: "active" })} data-testid="bonus-activate">Activate</Button>}
          {canManage && !v.official && v.status !== "archived" && <Button variant="ghost" onClick={() => save({ status: "archived" })} data-testid="bonus-archive">Archive</Button>}
          {!locked && <Button onClick={() => save()} className="bg-[#006090] hover:bg-[#004E75]" data-testid="bonus-save"><Save className="w-4 h-4 mr-1.5" />Save</Button>}
        </div>
      </div>
      {locked && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="bonus-locked">
        <Lock className="w-3.5 h-3.5" />{v.official ? "The official schedule is read-only." : v.participants ? "Assigned to participants, so requirements are locked." : "Read-only."} Copy it to a new year to make changes.</p>}
      <div className="overflow-x-auto rounded-xl border border-slate-300 bg-white">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-[#003A70] text-white text-xs"><tr><th className="px-2 py-2.5 text-left">Performance Bonus Opportunity</th>
            {NUM.map(([k, l]) => <th key={k} className="px-2 py-2.5">{l}</th>)}<th className="px-2 py-2.5">Training Completed</th></tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={r.key} className="border-t border-slate-200" data-testid={`bonus-row-${r.key}`}>
              <td className="px-2 py-2">{locked ? <span className="font-bold text-[#003A70]">{r.label}</span> : <Input value={r.label} onChange={(e) => set(i, "label", e.target.value)} className="h-8" />}</td>
              {NUM.map(([k]) => <td key={k} className="px-2 py-2 text-center tabular-nums">{locked ? (r[k] == null ? "—" : `${k === "folio_bonus" ? r.folio_prefix || "" : ""}${Number(r[k]).toLocaleString()}`)
                : <Input type="number" min="0" value={r[k] ?? ""} onChange={(e) => set(i, k, e.target.value)} className="h-8 w-24 mx-auto text-right" data-testid={`bonus-${r.key}-${k}`} />}</td>)}
              <td className="px-2 py-2 text-center">{locked ? (r.training ? "Yes" : "—") : <input type="checkbox" checked={!!r.training} onChange={(e) => set(i, "training", e.target.checked)} data-testid={`bonus-${r.key}-training`} />}</td>
            </tr>))}</tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">Blank cells mean the requirement doesn't apply to that row ("—" in the official table). Participants' monthly results are stored separately and are never changed by schedule edits.</p>
    </div>
  );
}

export default function BonusSchedules() {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  const [copy, setCopy] = useState(false);
  const [prog, setProg] = useState("associate");
  const load = useCallback((pick) => {
    api.get(`/${prog}-bonus/versions`).then((r) => { setD(r.data); setSel((s) => (pick || (r.data.items.some((x) => x.id === s) ? s : r.data.items[0]?.id))); }).catch((e) => toast.error(apiError(e)));
  }, [prog]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-sm text-slate-400">Loading…</p>;
  const v = d.items.find((x) => x.id === sel);
  return (
    <div className="space-y-6" data-testid="bonus-schedules">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><Award className="w-6 h-6 text-[#006090]" />Program Bonus Schedules</h1>
        <p className="text-sm text-slate-500 mt-1">Annual bonus program requirements. Each year is its own version; participants keep the schedule they're assigned to.</p>
      </div>
      <div className="inline-flex rounded-lg bg-slate-100 p-1" data-testid="bonus-program-switch">
        {[["associate", "Associate Agent Bonus Program"], ["protege", "Protégé Agency Producer Program"]].map(([k, l]) => (
          <button key={k} onClick={() => { setProg(k); setSel(null); }} data-testid={`bonus-program-${k}`}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${prog === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>))}
      </div>
      <div className="flex flex-wrap gap-2" data-testid="bonus-version-list">{d.items.map((x) => (
        <button key={x.id} onClick={() => setSel(x.id)} data-testid={`bonus-version-${x.year}-${x.status}`}
          className={`rounded-xl border px-3 py-2 text-left text-sm ${x.id === sel ? "border-[#006090] bg-[#006090]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}>
          <p className="font-semibold text-slate-900">{x.year}{x.official ? " · Official" : ""}</p><p className="text-xs text-slate-500">{STATUS[x.status] || x.status} · {x.participants} assigned</p>
        </button>))}</div>
      {v && <Editor v={v} canManage={d.can_manage} onSaved={(id) => load(id)} onCopy={() => setCopy(true)} />}
      {copy && v && <CopyDialog src={v} onClose={() => setCopy(false)} onDone={(id) => { setCopy(false); load(id); }} />}
    </div>
  );
}
